import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import util from 'preact-util';
import md5 from 'crypto-js/md5';

import { classifyClientOrientation } from '../../lib/shorts';

// Compose-and-post sheet for a freshly captured/picked snutt. The upload
// starts immediately on mount and runs while the user types the caption;
// Publish unlocks when the upload lands. The article pipeline (Lambda
// transcode, orientation stamping, activation) is untouched phase-1
// machinery - a portrait clip appears in the shorts feed only after
// processing, which is why the success state says so instead of closing
// straight into a feed that cannot show the clip yet.
@observer
class ShortsCaptureDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            phase: 'uploading', // uploading | compose | posting | done | error
            progress: 0,
            uploadedFile: null,
            caption: '',
            orientation: null,
            errorType: null, // 'upload' | 'post'
        };
        this.previewUrl = null;
    }

    componentDidMount() {
        const { fileObject } = this.props;
        if (fileObject) {
            this.previewUrl = URL.createObjectURL(fileObject);
            this.uploadFile();
        }
    }

    componentWillUnmount() {
        if (this.previewUrl) {
            URL.revokeObjectURL(this.previewUrl);
            this.previewUrl = null;
        }
    }

    onPreviewMetadata = (e) => {
        const { videoWidth, videoHeight } = e.target;
        this.setState({ orientation: classifyClientOrientation(videoWidth, videoHeight) });
    }

    uploadFile = () => {
        const { fileObject } = this.props;
        this.setState({ phase: 'uploading', progress: 0, errorType: null });

        // XHR instead of util.fetchApi: upload progress events need it.
        // Same request shape as form/dragNdropFileupload.js (field files[],
        // Bearer token, 201 + data.files on success).
        const formData = new FormData();
        formData.append('files[]', fileObject, encodeURIComponent(fileObject.name));

        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
                this.setState({ progress: Math.round((event.loaded / event.total) * 100) });
            }
        });
        xhr.addEventListener('readystatechange', () => {
            if (xhr.readyState !== 4) {
                return;
            }
            if (xhr.status === 201) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    const file = response?.data?.files?.[0];
                    if (file) {
                        // Same stamping as form/files.js handleAddImage.
                        this.setState({
                            phase: 'compose',
                            uploadedFile: {
                                ...file,
                                md5: md5(JSON.stringify(file)).toString(),
                                date: new Date(),
                            },
                        });
                        return;
                    }
                } catch (err) {
                    // Malformed response body - treated as upload failure below.
                }
            }
            this.setState({ phase: 'error', errorType: 'upload' });
        });
        xhr.addEventListener('error', () => {
            this.setState({ phase: 'error', errorType: 'upload' });
        });
        xhr.open('POST', `${util.getApiServer()}/api/fileupload`);
        xhr.setRequestHeader('Authorization', `Bearer ${util.getJwtToken()}`);
        xhr.send(formData);
    }

    onCaptionInput = (e) => {
        this.setState({ caption: e.target.value });
    }

    postClip = async () => {
        const { articleStore } = this.props.stores;
        const { uploadedFile, caption } = this.state;
        this.setState({ phase: 'posting' });
        const response = await articleStore.insert({
            category: 'feed',
            active: 1,
            ingress: caption,
            files: [uploadedFile],
            hostname: articleStore.currentHostname,
        });
        const article = response?.data?.article;
        if (article?.id) {
            if (article.videoStatus === 'processing') {
                articleStore.pollVideoStatus(article.id);
            }
            this.setState({ phase: 'done' });
        } else {
            this.setState({ phase: 'error', errorType: 'post' });
        }
    }

    retry = () => {
        if (this.state.errorType === 'post') {
            this.postClip();
        } else {
            this.uploadFile();
        }
    }

    closeDrawer = () => {
        const { drawerLevel } = this.props;
        const { appState } = this.props.stores;
        appState.toggleDrawer(false, drawerLevel);
    }

    render() {
        const { phase, progress, caption, orientation, errorType } = this.state;

        return (
            <div class='w-100 h-100 d-flex flex-column bg-body text-body px-3 pt-3 pb-4' style='overflow-y: auto;'>
                <h5 class='mb-3'>
                    <i class='fa-solid fa-clapperboard-play me-2' />
                    <Text id='shorts.new-clip-title'>New clip</Text>
                </h5>

                {phase === 'done' ? <>
                    <div class='alert alert-success'>
                        <i class='fa-solid fa-circle-check me-2' />
                        <Text id='shorts.published-processing'>Published! Your clip will appear once the video has finished processing.</Text>
                    </div>
                    <button type='button' class='btn btn-primary' onClick={this.closeDrawer}>
                        <Text id='shorts.done'>Done</Text>
                    </button>
                </> : <>
                    <video
                        src={this.previewUrl}
                        class='w-100 rounded mb-2'
                        style='max-height: 40vh; object-fit: contain; background: #000;'
                        muted
                        loop
                        autoPlay
                        playsinline
                        webkit-playsinline='true'
                        onLoadedMetadata={this.onPreviewMetadata}
                    />

                    {orientation && orientation !== 'portrait' && (
                        <div class='alert alert-info py-2 small'>
                            <i class='fa-solid fa-circle-info me-2' />
                            <Text id='shorts.landscape-hint'>This looks like a landscape video – it will show up under Videos instead of Clips.</Text>
                        </div>
                    )}

                    {phase === 'uploading' && (
                        <div class='mb-2'>
                            <small class='text-body-secondary'>
                                <Text id='shorts.uploading'>Uploading video…</Text> {progress}%
                            </small>
                            <div class='progress' style='height: 6px;'>
                                <div class='progress-bar' style={`width: ${progress}%;`} />
                            </div>
                        </div>
                    )}

                    {phase === 'error' && (
                        <div class='alert alert-danger py-2'>
                            <i class='fa-solid fa-triangle-exclamation me-2' />
                            {errorType === 'post' ? (
                                <Text id='shorts.post-failed'>Could not publish the clip</Text>
                            ) : (
                                <Text id='shorts.upload-failed'>Could not upload the video</Text>
                            )}
                            <button type='button' class='btn btn-sm btn-outline-danger ms-2' onClick={this.retry}>
                                <Text id='shorts.retry'>Try again</Text>
                            </button>
                        </div>
                    )}

                    <Localizer>
                        <textarea
                            class='form-control mb-3'
                            rows={3}
                            value={caption}
                            onInput={this.onCaptionInput}
                            placeholder={<Text id='shorts.caption-placeholder'>Write a caption…</Text>}
                        />
                    </Localizer>

                    <button
                        type='button'
                        class='btn btn-primary'
                        disabled={phase !== 'compose'}
                        onClick={this.postClip}
                    >
                        {phase === 'posting' && (
                            <span class='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true' />
                        )}
                        <Text id='shorts.post'>Publish</Text>
                    </button>
                </>}
            </div>
        );
    }
}

export default ShortsCaptureDrawer;
